import { useState } from 'react';

import { Filter, TestType } from '@qality/common-model';
import { TestCaseInCycle } from '@qality/common-model/src/TestCaseInCycle';
import { StatusSelectOption, StringUtils } from '@qality/common-utils';

import { AssigneeOptions } from '../testCasesInCycleList/TestCaseInCycleAssigneeFilter';

type TestCaseInCycleFilter = Filter<TestCaseInCycle>;

export function useTestCaseFilter() {
  const [textSearchFilter, setTextSearchFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeOptions>([]);
  const [statusFilter, setStatusFilter] = useState<StatusSelectOption[]>([]);
  const [testTypeFilter, setTestTypeFilter] = useState<Array<TestType>>([]);

  const filterByText: TestCaseInCycleFilter = (testCaseInCycle) => {
    const sanitizedTextSearchFilter =
      StringUtils.removeWhitespace(textSearchFilter).toLowerCase();

    const isTextSearchFilterEmpty = sanitizedTextSearchFilter.length === 0;

    if (isTextSearchFilterEmpty) {
      return true;
    }

    const hasPermissionForTestCase =
      checkHasPermissionForTestCase(testCaseInCycle);

    if (!hasPermissionForTestCase) {
      return false;
    }

    const sanitizedSummary = StringUtils.removeWhitespace(
      testCaseInCycle.testCase.fields.summary
    ).toLowerCase();

    const sanitizedKey = testCaseInCycle.testCase.key.toLowerCase();

    return (
      sanitizedSummary.includes(sanitizedTextSearchFilter) ||
      sanitizedKey.includes(sanitizedTextSearchFilter)
    );
  };

  const filterByAssignee: TestCaseInCycleFilter = (testCaseInCycle) => {
    const isAssigneeFilterEmpty = assigneeFilter.length === 0;

    if (isAssigneeFilterEmpty) {
      return true;
    }

    const hasPermissionForTestCase =
      checkHasPermissionForTestCase(testCaseInCycle);

    if (!hasPermissionForTestCase) {
      return false;
    }

    const { accountId: executionAssigneeAccountId = null } =
      testCaseInCycle.testExecution?.executionAssignee ?? {};

    const isExecutionAssigneeInFilter =
      assigneeFilter.find((assigneeFilterItem) => {
        const { value: assigneeFilterItemAccountId } = assigneeFilterItem;

        return assigneeFilterItemAccountId === executionAssigneeAccountId;
      }) !== undefined;

    return isExecutionAssigneeInFilter;
  };

  const filterByStatus: TestCaseInCycleFilter = (testCaseInCycle) => {
    const isStatusFilterEmpty = statusFilter.length === 0;

    if (isStatusFilterEmpty) {
      return true;
    }

    const isExecutionStatusInFilter =
      statusFilter.find((statusFilterItem) => {
        return (
          String(statusFilterItem.value) ===
          String(testCaseInCycle.statusDetail.id)
        );
      }) !== undefined;

    return isExecutionStatusInFilter;
  };

  const filterByTestType: TestCaseInCycleFilter = (testCaseInCycle) => {
    const isTestTypeFilterEmpty = testTypeFilter.length === 0;

    if (isTestTypeFilterEmpty) {
      return true;
    }

    const isTestTypeInFilter =
      testTypeFilter.find((testTypeFilterItem) => {
        return testTypeFilterItem.id === testCaseInCycle.testType.id;
      }) !== undefined;

    return isTestTypeInFilter;
  };

  const filterTestCaseFunction: TestCaseInCycleFilter = (testCaseInCycle) => {
    return [filterByText, filterByAssignee, filterByStatus, filterByTestType]
      .map((filterFunction) => filterFunction(testCaseInCycle))
      .every(Boolean);
  };

  const checkHasPermissionForTestCase = (
    testCaseInCycle: TestCaseInCycle
  ): boolean => {
    return Boolean(testCaseInCycle.testCase);
  };

  const isTestCaseFilterActive = [
    textSearchFilter !== '',
    assigneeFilter.length > 0,
    statusFilter.length > 0,
  ].some(Boolean);

  return {
    textSearchFilter,
    assigneeFilter,
    statusFilter,
    testTypeFilter,
    isTestCaseFilterActive,
    filterTestCaseFunction,
    setTextSearchFilter: (value: string) => {
      setTextSearchFilter(value);
    },
    setAssigneeFilter: (value: AssigneeOptions) => {
      setAssigneeFilter(value);
    },
    setStatusFilter: (value: StatusSelectOption[]) => {
      setStatusFilter(value);
    },
    setTestTypeFilter: (value: Array<TestType>) => {
      setTestTypeFilter(value);
    },
  };
}
